import { Fragment, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { DateTime } from "luxon";
import { Formik } from "formik";

import { faAt, faPhone, faSignInAlt } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useParams } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/20/solid";

import { ButtonSecondaryModal } from "@/shared/components/atoms/ButtonSecondaryModal";
import { InputWithLeadingIcon } from "@/shared/components/atoms/InputWithLeadingIcon";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { ButtonPrimaryModal } from "@/shared/components/atoms/ButtonPrimaryModal";
import { PersonalListItem } from "@/shared/components/molecules/PersonalListItem";
import AlertErrorButtons from "@/shared/components/molecules/AlertErrorButtons";
import { ButtonSecondary } from "@/shared/components/atoms/ButtonSecondary";
import { SelectCountry } from "@/shared/components/organisms/SelectCountry";
import { PersonalList } from "@/shared/components/organisms/PersonalList";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import PaginationUI from "@/shared/components/atoms/PaginationUI";
import { Calendar } from "@/shared/components/organisms/Calendar";
import { ModalXXL } from "@/shared/components/organisms/ModalXXL";
import { InputFile } from "@/shared/components/atoms/InputFile";
import { Select } from "@/shared/components/organisms/Select";
import { TextArea } from "@/shared/components/atoms/TextArea";
import { Modal } from "@/shared/components/organisms/Modal";
import { Empty } from "@/shared/components/organisms/Empty";
import { useGetAllStatesQuery } from "@/app/app.apiStates";
import { Avatar } from "@/shared/components/atoms/Avatar";
import { toasts } from "@/shared/utils/toasts";

import { User } from "@/app/auth/auth";

import { Permission, usePermission } from "@/shared/hooks/usePermission";

import {
  personalInitialValues,
  personalValidationSchema,
} from "@/shared/schemas/personal.schema";
import {
  useChangePasswordMutation,
  useCreateSessionMutation,
} from "@/app/auth/auth.api";
import { setAccessToken } from "@/app/auth/auth.slice";
import {
  usePersonalQuery,
  useDeleteImageMutation,
  useUpdatePersonalMutation,
  useCreatePersonalMutation,
  useDeletePersonalMutation,
  useGetPersonalRolesQuery,
} from "@/app/branchs/branchs.api";
import { useRolesQuery } from "@/app/roles/roles.api";
import { useLessonsUserQuery } from "@/app/lessons/lessons.api";
import { useGetAllCountriesQuery } from "@/app/app.apiCountry";
import { useDebounce } from "@/shared/hooks/debounce";

DateTime.local().setLocale("es");

type Person = User & {
  profile_avatar: File | null;
  name: string;
  role: string;
  phone: string;
  email: string;
  address: string;
  country: string;
  state: string;
  city: string;
  branch_id: number;
  gym_id: number;
  dni: string;
  occupation: string;
  occupation_description: string;
  company: string;
  birthday: string;
  is_chat_responsible: boolean | number;
  sex: string;
  objective: string;
  terms: boolean;
  accept_clauses: boolean;
  sufficiently_informed: boolean;
  receive_communications: boolean;
  image_rights: boolean;
  personalInformation: PersonalInformation;
};

interface PersonalInformation {
  acceptClauses: string;
  address: string;
  age: number;
  birthday: string;
  city: string;
  country: string;
  company: string;
  createdAt: string;
  dateIni: string;
  dni: string;
  id: number;
  imageRights: string;
  occupation: string;
  occupationDescription: string;
  period: number;
  postalCode: string;
  receiveCommunications: string;
  sex: string;
  state: string;
  status: number;
  sufficientlyInformed: string;
  typeDni: number;
  updatedAt: string;
  user: User;
  userId: number;
}

export type PersonalPageProps = any;

export const PersonalPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermission();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [openAlertDelete, setOpenAlertDelete] = useState(false);
  const [currentPersonalResetPassword, setCurrentPersonalResetPassword] =
    useState<User | undefined>(undefined);
  const [currentPersonal, setCurrentPersonal] = useState<Person | undefined>(
    undefined
  );
  const [currentPersonalCalendar, setCurrentPersonalCalendar] = useState<
    User | undefined
  >(undefined);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [currentPerson, serCurrentPerson] = useState<Person | undefined>(
    undefined
  );

  const optionsSex = [
    { id: "Hombre", name: t("user.views.man") },
    { id: "Mujer", name: t("user.views.female") },
  ];

  const optionOccupation = [
    { id: "coach", name: t("user.views.coach1") },
    {
      id: "nutritionist",
      name: t("user.views.nutritionist"),
    },
    {
      id: "psychologist",
      name: t("user.views.psychologist"),
    },
    {
      id: "physiotherapist",
      name: t("user.views.physiotherapist"),
    },
  ];

  const optionsObjetives = [
    {
      id: "healt",
      name: t("progress.objective.healt"),
    },
    {
      id: "definition",
      name: t("progress.objective.definition"),
    },
    {
      id: "hypertrophy",
      name: t("progress.objective.hypertrophy"),
    },
  ];
  const debouncedSearch = useDebounce(search, 500);

  const personalQuery = usePersonalQuery({
    branchId: id,
    search: debouncedSearch,
    page: debouncedSearch ? 1 : page,
  });
  const [changePassword, changePasswordResult] = useChangePasswordMutation();
  const [createPersonal, createPersonalResult] = useCreatePersonalMutation();
  const [updatePersonal, updatePersonalResult] = useUpdatePersonalMutation();
  const [deletePersonal, deletePersonalResult] = useDeletePersonalMutation();
  const [createSession, createSessionResult] = useCreateSessionMutation();

  const [deleteImage, deleteImageResult] = useDeleteImageMutation();

  //Controlar Abre/Cierre Dialog
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (currentPerson) {
      setOpen(true);
    }
  }, [currentPerson]);

  useEffect(() => {
    if (createSessionResult.isSuccess) {
      localStorage.setItem("accessToken", createSessionResult.data.accessToken);

      dispatch(setAccessToken({ accessToken: createSessionResult.data }));
    }
  }, [createSessionResult]);

  useEffect(() => {
    if (changePasswordResult.isSuccess) {
      toasts.success(
        "Cambios realizados",
        "La contraseña ha sido cambiada exitosamente"
      );
    }

    if (changePasswordResult.isError) {
      toasts.danger(
        "Cambios no realizados",
        "Ocurrió un error al cambiar la contraseña"
      );
    }
  }, [changePasswordResult]);

  useEffect(() => {
    if (createPersonalResult.isSuccess) {
      toasts.success(
        "Registro exitoso",
        "El personal ha sido creado correctamente"
      );

      //refrescar la lista de usuarios
      personalQuery.refetch();
    }

    if (createPersonalResult.isError) {
      toasts.danger(
        "Cambios no realizados",
        "Ocurrió un error al crear el usuario"
      );
    }
  }, [createPersonalResult]);

  useEffect(() => {
    if (updatePersonalResult.isSuccess) {
      toasts.success(
        "Cambios realizados",
        "El usuario ha sido actualizado correctamente"
      );
    }

    if (updatePersonalResult.isError) {
      toasts.danger(
        "Cambios no realizados",
        "Ocurrió un error al actualizar el usuario"
      );
    }
  }, [updatePersonalResult]);

  useEffect(() => {
    if (deletePersonalResult.isSuccess) {
      setCurrentPersonal(undefined);

      // Verificar si la página actual quedó vacía y regresar a la página 1
      if (personalQuery.data?.data?.length === 1 && page > 1) {
        setPage(1);
      }
    }
  }, [deletePersonalResult, personalQuery.data?.data?.length, page]);

  //TRAER PAISES PARA SELECT COUNTRIES
  const {
    data: countries,
    isLoading: isLoadingCountries,
    isError: isErrorCountries,
  } = useGetAllCountriesQuery({});

  //const rolesQuery = useRolesQuery({ branchId: id });
  const rolesQuery = useGetPersonalRolesQuery({ branchId: id });
  const roleOptions = useMemo(() => {
    return rolesQuery.data?.data?.map((role: any) => ({
      id: role.name,
      name: role.name,
    }));
  }, [rolesQuery]);

  // TRAER ESTADOS DE LOS PAISES
  const {
    data: states,
    isLoading: isLoadingStates,
    isError: isErrorStates,
  } = useGetAllStatesQuery({});

  const lessonsQuery = useLessonsUserQuery({
    branchId: id,
    userId: currentPersonalCalendar?.id,
  });

  //STATE PARA DARLE FORMATO A LOS EST
  const [optionsStateApi, setOptionsStateApi] = useState([]);
  const [selectedState, setSelectedState] = useState({ id: "", name: "" });

  //State para los paises
  const [SelectedCountry, SetSelectedCountry] = useState();
  const [userCountry, setUserCountry] = useState<string | null>(null);

  //STATE PARA DARLE FORMATO A LOS PAISES
  const [optionsCountryApi, setOptionsCountryApi] = useState([]);

  useEffect(() => {
    // Verifica que no esté cargando y que no haya errores
    if (
      !isLoadingCountries &&
      !isErrorCountries &&
      countries &&
      countries.length > 0
    ) {
      // Mapea y transforma los datos de los países
      const arrayCountries = countries.map((country) => ({
        id: country.name.common,
        name: country.name.common,
        code: country.cca2,
      }));

      // agregar un país por defecto
      arrayCountries.unshift({ id: "", name: "Selecciona un país", code: "" });

      // Actualiza el estado con los países formateados
      setOptionsCountryApi(arrayCountries);
    }

    // Manejamos los errores
    if (isErrorCountries) {
      console.log("ERROR EN LOS COUNTRIES");
    }
  }, [countries, isLoadingCountries, isErrorCountries]);

  useEffect(() => {
    if (optionsStateApi.length > 0) {
      // Verifica que haya estados disponibles antes de seleccionar el primero
      setSelectedState(optionsStateApi[0]);
    }
  }, [SelectedCountry, optionsStateApi]);

  const handleClose = () => {
    serCurrentPerson(undefined);
  };

  const handleDeleteImage = (idUser) => {
    deleteImage({ id: idUser });
  };

  useEffect(() => {
    if (deleteImageResult.isSuccess) {
      toasts.success(
        "Imagen eliminada",
        "La imagen ha sido eliminada correctamente"
      );
    }

    if (deleteImageResult.isError) {
      toasts.danger(
        "Imagen no eliminada",
        "Ocurrió un error al eliminar la imagen"
      );
    }
  }, [deleteImageResult]);

  const handleManageNewSession = async () => {
    try {
      await createSession({ userId: currentPersonal?.id });
      navigate(`/users`, { replace: true });
    } catch (error) {
      console.log("error", error);
      toasts.danger(
        "Error al iniciar sesión",
        "Ocurrió un error al iniciar sesión"
      );
    }
  };

  const createOrEditValuesPersonal = useMemo(() => {
    return currentPerson?.id
      ? {
          ...currentPerson,
          address: currentPerson.personalInformation.address || "",
          country: currentPerson.personalInformation.country || "",
          city: currentPerson.personalInformation.city || "",
          state: currentPerson.personalInformation.state || "",
          birthday: currentPerson.personalInformation.birthday || "",
          postal_code: currentPerson.personalInformation.postalCode || "",
          dni: currentPerson.personalInformation.dni || "",
          occupation: currentPerson.personalInformation.occupation || "",
          company: currentPerson.personalInformation.company || "",
          sex: currentPerson.personalInformation.sex || "",
          objective: currentPerson.objective || "",
          occupation_description:
            currentPerson.personalInformation.occupationDescription || "",
          is_chat_responsible:
            currentPerson.isChatResponsible === 1 ? true : false || false,
          accept_clauses: true,
          image_rights: currentPerson.image_rights,
          sufficiently_informed: true,
          receive_communications: currentPerson.receive_communications
            ? true
            : false,
        }
      : personalInitialValues;
  }, [currentPerson?.id]);

  // Check if current page is greater than 1 and there are no items on current page
  useEffect(() => {
    if (personalQuery?.data?.data?.data && page > 1) {
      const currentPageData = personalQuery.data.data.data;
      const totalPages = personalQuery.data.data.lastPage;

      // If current page has no data and we're not on page 1, go back to page 1
      if (currentPageData.length === 0 && page > 1) {
        setPage(1);
      }
    }
  }, [personalQuery.data?.data, page]);

  return (
    <div className="flex flex-col flex-1 h-0 w-full overflow-y-scroll pt-8">
      <div className="flex flex-1 flex-col h-0 w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
        <div className="flex-1 px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center">
            <div className="flex-grow">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                {t("pages.personal.title")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("pages.personal.subtitle")}
              </p>
            </div>
            <div className="mt-4 sm:ml-16 sm:mt-0"></div>
          </div>
          {/* Divider debajo del texto */}
          <div className="border-b border-gray-300 my-3"></div>
          <div className="sm:flex sm:items-center">
            <div className="sm:flex md:w-64 lg:w-80">
              <InputWithCornerHint
                label={t("general.search")}
                name="search"
                type="text"
                className="w-full"
                placeholder={t("pages.personal.title")}
                error={false}
                onChange={(event) => setSearch(event?.target?.value)}
                value={search}
              />
            </div>

            <div className="mt-8 sm:ml-auto sm:mt-4 sm:flex sm:items-center sm:space-x-4 "></div>
            <div className="mt-8 sm:ml-auto sm:mt-4 flex items-center space-x-4 ">
              {can(Permission.BRANCH_ACCESS_CREATE_EDIT_STAFF) && (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
                >
                  <PlusIcon aria-hidden="true" className="h-5 w-5" />
                </button>
              )}
              {/* 
              Button reidrect to My Subscription Fitmewise, I'm comment this button cause the implementation will be in the future
              <ButtonSecondary
                text={t("pages.subscriptions.title")}
                onClick={() =>
                  navigate(`/branchs/${id}/settings/subscriptions`)
                }
                size="large"
              /> */}
            </div>
          </div>
          <div className="border-b border-gray-300 my-3"></div>
          <div className="flex flex-1">
            {personalQuery.isSuccess &&
              personalQuery.data?.data?.length == 0 && (
                <div className="flex-1">
                  <Empty
                    onClick={() => setOpen(true)}
                    texto={t("pages.personal.empty")}
                  />
                </div>
              )}

            {personalQuery.isLoading && (
              <PersonalList>
                <Fragment>
                  {Array.from({ length: 8 }).map((_, index) => (
                    <PersonalListItem isLoading key={index} />
                  ))}
                </Fragment>
              </PersonalList>
            )}
            {personalQuery.data?.data?.data?.length > 0 && (
              <PersonalList>
                {personalQuery?.data?.data?.data?.map((user: User) => (
                  <PersonalListItem
                    user={user}
                    onActivate={can(
                      Permission.BRANCH_ACCESS_ACTIVATE_DISABLE_STAFF,
                      () =>
                        updatePersonal({
                          id: user.id,
                          active: user.active == 1 ? 0 : 1,
                        })
                    )}
                    onResetPassword={can(
                      Permission.BRANCH_ACCESS_RESET_BRANCH_PASSWORD,
                      () => setCurrentPersonalResetPassword(user)
                    )}
                    onCalendar={() => setCurrentPersonalCalendar(user)}
                    onDetails={() => setCurrentPersonal(user)}
                  />
                ))}
              </PersonalList>
            )}
          </div>
          <div className="mt-4">
            <PaginationUI
              onPageChange={setPage}
              currentPage={page}
              pageSize={personalQuery?.data?.data?.perPage}
              totalResults={personalQuery?.data?.data?.total}
              totalPages={personalQuery?.data?.data?.lastPage}
            />
          </div>
          <AlertErrorButtons
            open={openAlertDelete}
            onClose={() => setOpenAlertDelete(!openAlertDelete)}
            title={t("pages.personal.title_delete")}
            message={t("pages.personal.message_delete")}
            primaryButtonText={t("general.delete")}
            secondaryButtonText={t("general.cancel")}
            onPrimaryButtonClick={async () => {
              try {
                await deletePersonal({
                  branchId: id,
                  id: currentPersonal?.id,
                }).unwrap();
                toasts.success(
                  "Personal eliminado",
                  "El personal se ha eliminado correctamente"
                );

                setOpenAlertDelete(!openAlertDelete);
              } catch (error) {
                toasts.danger(
                  "Cambios no realizados",
                  "Ocurrió un error al eliminar el usuario"
                );
              }
            }}
            onSecondaryButtonClick={() => {
              setOpenAlertDelete(false);
            }}
            modalClassName="z-[60]"
            isLoading={deletePersonalResult.isLoading}
          />
          <Modal
            open={!!currentPersonal}
            Close={() => setCurrentPersonal(undefined)}
            onClose={() => setCurrentPersonal(undefined)}
            tittleModal={currentPersonal?.name}
            modalClassName="z-50"
            footer={
              <div className="flex gap-2">
                {can(Permission.BRANCH_ACCESS_CREATE_EDIT_STAFF) && (
                  <ButtonSecondaryModal
                    text="Editar"
                    onClick={() => {
                      serCurrentPerson({
                        ...currentPersonal,
                        id: currentPersonal.id,
                        branch_id: currentPersonal.branch_id
                          ? currentPersonal.branch_id
                          : 1,
                        gym_id: 1,
                        is_chat_responsible:
                          currentPersonal.isChatResponsible === 1 ? 1 : 0,
                        image_rights:
                          currentPersonal.image_rights == "on" ? true : false,
                        profile_avatar: currentPersonal.avatar
                          ? currentPersonal.avatar
                          : null,
                        phone: currentPersonal.phone
                          ? currentPersonal.phone
                          : "",
                        accept_clauses:
                          currentPersonal.personalInformation.acceptClauses ==
                          "on"
                            ? true
                            : false,
                        sufficiently_informed:
                          currentPersonal.personalInformation
                            .sufficientlyInformed == "on"
                            ? true
                            : false,
                        objective:
                          typeof currentPersonal.objective == "string"
                            ? currentPersonal.objective
                            : "",
                        occupation: currentPersonal.occupation
                          ? currentPersonal.occupation
                          : "",
                        receive_communications:
                          currentPersonal.personalInformation
                            .receiveCommunications == "on"
                            ? true
                            : false,
                        image_rights:
                          currentPersonal.personalInformation.imageRights ==
                          "on"
                            ? true
                            : false,
                      });
                      setCurrentPersonal(undefined);
                    }}
                  />
                )}
                {can(Permission.BRANCH_ACCESS_CREATE_EDIT_STAFF) && (
                  <ButtonSecondaryModal
                    text="Eliminar"
                    onClick={() => setOpenAlertDelete(!openAlertDelete)}
                  />
                )}
                {can(Permission.BRANCH_ACCESS_CHANGE_STAFF_BRANCH) && (
                  <button
                    type="button"
                    className="hover:text-gray-500 inline-flex justify-center rounded-md bg-secondary-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-secondary-400"
                    onClick={handleManageNewSession}
                  >
                    <span className="sr-only">LogIn</span>
                    <FontAwesomeIcon
                      icon={faSignInAlt}
                      aria-hidden="true"
                      className="h-6 w-6"
                    />
                  </button>
                )}

                <ButtonPrimaryModal
                  text="Ver usuario"
                  onClick={() => {
                    navigate(`/users/${currentPersonal?.id}`);
                  }}
                />
              </div>
            }
          >
            <div className="flex gap-4">
              <button
                type="button"
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                onClick={() => setCurrentPersonal(undefined)}
              >
                <span className="sr-only">Cerrar</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <Avatar src={currentPersonal?.avatar} size="huge" />
              <dl>
                <div className="py-2 sm:grid sm:grid-cols-3">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("forms.label.name")}
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {currentPersonal?.name ?? "-"}
                  </dd>
                </div>
                <div className="bg-white sm:grid sm:grid-cols-3">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("forms.label.email")}
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {currentPersonal?.email ?? "-"}
                  </dd>
                </div>
                <div className="py-2 sm:grid sm:grid-cols-3">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("forms.label.phone")}
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {currentPersonal?.phone ?? "-"}
                  </dd>
                </div>
                <div className="bg-white sm:grid gap-2 sm:grid-cols-3">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("forms.label.createdAt")}
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {DateTime.fromISO(currentPersonal?.createdAt)
                      .setLocale("es")
                      .toRelativeCalendar() ?? "-"}
                  </dd>
                </div>
                <div className="py-2 sm:grid sm:grid-cols-3">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("forms.label.occupation")}
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {currentPersonal?.occupation ?? "-"}
                  </dd>
                </div>
                <div className="py-2 sm:grid sm:grid-cols-3">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("general.calendar")}
                  </dt>
                  <dd className="text-sm text-gray-900">
                    <a
                      href="#"
                      onClick={() => {
                        setCurrentPersonal(undefined);
                        setCurrentPersonalCalendar(currentPersonal);
                      }}
                    >
                      Ver
                    </a>
                  </dd>
                </div>
              </dl>
            </div>
          </Modal>
          <ModalXXL
            tittleModal="Calendario"
            open={!!currentPersonalCalendar}
            Close={() => setCurrentPersonalCalendar(undefined)}
            modalClassName="z-50"
          >
            <div className="w-full flex justify-between">
              <div className="flex gap-2">
                <span className="flex gap-2">
                  <span>{t("calendar.completed_lessons")}</span>
                  <span id="lesson-count">
                    {lessonsQuery?.data ? lessonsQuery?.data.length : 0}
                  </span>
                </span>
                <span className="flex gap-2">
                  <span>{t("calendar.completed_assisteds")}</span>
                  <span id="assisted-count">0</span>
                </span>
              </div>
              <div className="flex justify-center gap-2">
                <div className="px-2 flex items-center h-8 border-2 border-solid border-primary">
                  <span>{t("calendar.lesson")}</span>
                </div>
                <div className="px-2 flex items-center h-8 border-2 border-dashed border-primary">
                  <span>{t("calendar.assisted")}</span>
                </div>
              </div>
            </div>

            <Calendar
              initialDateTime={DateTime.now()}
              events={lessonsQuery?.data ?? []}
              loading={lessonsQuery.isLoading || lessonsQuery.isFetching}
              onEventClick={() => {}}
              functionOpenModalActividades={() => {
                console.log("Open modal activities");
              }}
              hideFilter
            />
          </ModalXXL>
          <Formik
            initialValues={{ password: "" }}
            onSubmit={(values, formikHelpers) => {
              console.debug(currentPersonalResetPassword);
              changePassword({
                id: currentPersonalResetPassword?.id,
                password: values.password,
              });

              formikHelpers.resetForm();
              setCurrentPersonalResetPassword(undefined);
            }}
          >
            {({ handleChange, handleSubmit, values, errors }) => (
              <Modal
                Close={() => setCurrentPersonalResetPassword(undefined)}
                open={!!currentPersonalResetPassword}
                onClickCreate={handleSubmit}
                textButtonCreate={t("general.save")}
                textButtonClose={t("general.cancel")}
                tittleModal={t("user.views.reset_password")}
              >
                <div className="w-full">
                  <div className="w-full mt-3">
                    <InputWithCornerHint
                      label="Contraseña"
                      name="password"
                      type="text"
                      placeholder="Contraseña"
                      onChange={handleChange}
                      error={!!errors.password}
                      textError={errors.password}
                      value={values.password}
                    />
                  </div>
                </div>
              </Modal>
            )}
          </Formik>
          <Formik
            initialValues={createOrEditValuesPersonal}
            validationSchema={personalValidationSchema}
            enableReinitialize
            onSubmit={async (values, formikHelpers) => {
              const formToSend = new FormData();

              if (values.profile_avatar) {
                formToSend.append("profile_avatar", values.profile_avatar);
              }

              formToSend.append("name", values.name);
              formToSend.append("role", values.role);
              formToSend.append("phone", values.phone);
              formToSend.append("email", values.email);
              formToSend.append("address", values.address);
              formToSend.append("country", values.country);
              formToSend.append("state", values.state);
              formToSend.append("city", values.city);
              formToSend.append("postal_code", values.postal_code);
              formToSend.append("dni", values.dni);
              formToSend.append("occupation", values.occupation);
              formToSend.append(
                "occupation_description",
                values.occupation_description
              );
              formToSend.append("company", values.company);
              formToSend.append("birthday", values.birthday);
              formToSend.append("sex", values.sex);
              formToSend.append("objective", values.objective);
              formToSend.append("accept_clauses", "true");
              formToSend.append("sufficiently_informed", "true");
              formToSend.append(
                "receive_communications",
                values.receive_communications ? "true" : "false"
              );
              formToSend.append(
                "image_rights",
                values.image_rights ? "true" : "false"
              );
              formToSend.append(
                "is_chat_responsible",
                values.is_chat_responsible ? "true" : "false"
              );

              if (!currentPerson) {
                formToSend.append("branch_id", id);
              }

              if (currentPerson) {
                formToSend.append("id", currentPerson.id);

                console.log("Data to refresh: ", formToSend.values);

                await updatePersonal({
                  id: currentPerson.id,
                  personData: formToSend,
                }).unwrap();
              } else {
                await createPersonal(formToSend).unwrap();
              }

              formikHelpers.resetForm();
              setCurrentPersonal(undefined);
              serCurrentPerson(undefined);
              setOpen(false);
            }}
          >
            {({
              handleChange,
              setFieldValue,
              values,
              handleSubmit,
              errors,
              touched,
              resetForm,
            }) => {
              useEffect(() => {
                if (!isLoadingStates && !isErrorStates && states?.length > 0) {
                  if (values.country == undefined) {
                    setOptionsStateApi([
                      { id: "NO TIENE", name: "Sin estados" },
                    ]);
                    return;
                  }

                  if (!values.country) return;
                  const defaultCountry = optionsCountryApi.find(
                    (country) => country.id === values.country
                  );
                  const statesFilterByCode = states.filter(
                    (state) => state.countryCode === defaultCountry?.code
                  );

                  const arrayStates = statesFilterByCode.map((state) => ({
                    id: state.name,
                    name: state.name,
                  }));
                  if (arrayStates.length > 0) {
                    setOptionsStateApi(arrayStates);
                    SetSelectedCountry(defaultCountry);
                    setFieldValue("country", defaultCountry.id);
                  } else {
                    setOptionsStateApi([
                      { id: "NO TIENE", name: "Sin estados" },
                    ]);
                  }

                  // Asegúrate de que haya estados en el array antes de seleccionarlo
                  if (arrayStates.length > 0 && !selectedState) {
                    setSelectedState(arrayStates[0]);
                  }
                }
              }, [states, isLoadingStates, isErrorStates, values.country]);

              // Busqueda y seleccion del pais del usuario
              const fetchWithRetry = async (url, retries = 3, delay = 1000) => {
                for (let i = 0; i < retries; i++) {
                  try {
                    const response = await fetch(url);
                    if (!response.ok)
                      throw new Error("Network response was not ok");
                    return await response.json();
                  } catch (error) {
                    if (i < retries - 1) {
                      await new Promise((resolve) =>
                        setTimeout(resolve, delay)
                      );
                    } else {
                      throw error;
                    }
                  }
                }
              };

              useEffect(() => {
                const fetchUserCountry = async () => {
                  try {
                    const data = await fetchWithRetry(
                      "https://ipinfo.io/json?token=f824f0ad307047"
                    );
                    setUserCountry(data.country);
                    if (roleOptions) {
                      setFieldValue("role", roleOptions[0]);
                    }
                  } catch (error) {
                    console.error("Error fetching user country:", error);
                  }
                };

                fetchUserCountry();
              }, []);

              useEffect(() => {
                if (userCountry && optionsCountryApi.length > 0) {
                  const defaultCountry = optionsCountryApi.find(
                    (country) => country.code === userCountry
                  );
                  if (defaultCountry) {
                    setFieldValue("country", defaultCountry.id);
                  }
                }
              }, [userCountry, optionsCountryApi]);

              return (
                <DialogCreate
                  OnclickCreate={handleSubmit}
                  OnclickCancel={() => {
                    setOpen(false);
                    resetForm();
                  }}
                  tittleButtonCreate={
                    currentPerson ? t("general.edit") : t("general.create")
                  }
                  tittleButtonCancel={t("general.cancel")}
                  open={open}
                  setOpen={setOpen}
                  tittle={t("user.views.profile")}
                  subTittle={t("user.views.info_user")}
                  onClose={() => {
                    handleClose();
                    setOpen(false);
                    resetForm();
                  }}
                  isLoading={
                    rolesQuery.isLoading ||
                    createPersonalResult.isLoading ||
                    updatePersonalResult.isLoading
                  }
                >
                  <div className="mt-6 mx-2">
                    <div className="space-y-12">
                      <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div className="flex items-center justify-center h-full text-center">
                          <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                              {t("user.views.profil_user")}
                            </h2>
                          </div>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                          <div className="col-span-full">
                            <InputFile
                              name="profile_avatar"
                              id="profile_avatar"
                              label={t("user.views.avatar")}
                              spanButton={t("user.views.avatar")}
                              description={t("user.views.avatar")}
                              selectedImage={values.profile_avatar}
                              onFileChange={(file) => {
                                setFieldValue("profile_avatar", file);
                              }}
                              tittleDelete={t("general.delete_image")}
                              onRemoveImage={() => {
                                if (values.id !== 0) {
                                  handleDeleteImage(values.id);
                                }
                                setFieldValue("profile_avatar", null);
                              }}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("user.views.name")}
                              span={t("general.mandatory")}
                              id="name"
                              name="name"
                              type="text"
                              placeholder={t("user.views.name")}
                              value={values.name}
                              onChange={handleChange}
                              error={errors?.name && touched?.name}
                              textError={errors?.name}
                            />
                          </div>
                          <div className="col-span-full">
                            <Select
                              name="role"
                              span={t("general.mandatory")}
                              selected={
                                roleOptions
                                  ? roleOptions.find(
                                      (role) => role.name == values.role
                                    )
                                  : null
                              }
                              setSelected={(value) =>
                                setFieldValue("role", value.name)
                              }
                              options={roleOptions}
                              tittle={t("user.views.role")}
                              error={errors?.role && touched?.role}
                              textError={
                                Boolean(errors?.role) && Boolean(touched?.role)
                                  ? errors?.role
                                  : ""
                              }
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithLeadingIcon
                              label={t("user.views.phone")}
                              span={t("general.optional")}
                              id="phone"
                              name="phone"
                              type="text"
                              placeholder={t("user.views.phone")}
                              value={values.phone}
                              onChange={handleChange}
                              error={errors?.phone && touched?.phone}
                              textError={errors?.phone}
                              icon={
                                <FontAwesomeIcon
                                  icon={faPhone}
                                  className="h-4 w-4 text-gray-400"
                                />
                              }
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithLeadingIcon
                              label={t("user.views.email")}
                              span={t("general.mandatory")}
                              id="email"
                              name="email"
                              type="email"
                              placeholder={t("user.views.email")}
                              value={values.email}
                              onChange={handleChange}
                              error={errors?.email && touched?.email}
                              textError={errors?.email}
                              icon={
                                <FontAwesomeIcon
                                  icon={faAt}
                                  className="h-4 w-4 text-gray-400"
                                />
                              }
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("user.views.address")}
                              span={t("general.optional")}
                              id="address"
                              name="address"
                              type="text"
                              placeholder={t("user.views.address")}
                              value={values.address}
                              onChange={handleChange}
                              error={!!errors.address}
                              textError={errors.address}
                            />
                          </div>
                          <div className="col-span-full">
                            <SelectCountry
                              span={t("general.mandatory")}
                              selected={optionsCountryApi.find(
                                (country) => country.id === values.country
                              )}
                              setSelected={(value) => {
                                setFieldValue("country", value.id);
                                SetSelectedCountry(value as any);
                              }}
                              options={optionsCountryApi}
                              tittle={t("branch.views.country")}
                            />
                            {errors?.country && touched?.country ? (
                              <p className={`mt-1 text-sm text-red-600`}>
                                {errors?.country}
                              </p>
                            ) : null}
                          </div>
                          <div className="col-span-full">
                            <Select
                              name="state"
                              span={t("general.optional")}
                              selected={optionsStateApi.find(
                                (option) => option.id === values.state
                              )}
                              setSelected={(value) =>
                                setFieldValue("state", value.id)
                              }
                              options={optionsStateApi}
                              tittle={t("user.views.state")}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("user.views.city")}
                              span={t("general.optional")}
                              id="city"
                              name="city"
                              type="text"
                              placeholder={t("user.views.city")}
                              value={values.city}
                              onChange={handleChange}
                              error={!!errors.city}
                              textError={errors.city}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("user.views.postal_code")}
                              span={t("general.optional")}
                              id="postal_code"
                              name="postal_code"
                              type="text"
                              placeholder={t("user.views.postal_code")}
                              value={values.postal_code}
                              onChange={handleChange}
                              error={!!errors.postal_code}
                              textError={errors.postal_code}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("user.views.dni")}
                              span={t("general.optional")}
                              id="dni"
                              name="dni"
                              type="text"
                              placeholder={t("user.views.dni")}
                              value={values.dni}
                              onChange={handleChange}
                              error={!!errors.dni}
                              textError={errors.dni}
                            />
                          </div>
                          <div className="col-span-full">
                            <Select
                              name="occupation"
                              span={t("general.optional")}
                              selected={optionOccupation.find(
                                (occupation) =>
                                  occupation.id == values.occupation
                              )}
                              setSelected={(value) =>
                                setFieldValue("occupation", value.id)
                              }
                              options={optionOccupation}
                              tittle={t("user.views.occupation")}
                            />
                          </div>
                          <div className="col-span-full">
                            <TextArea
                              label={t("user.views.occupation_description")}
                              htmlFor="occupation_description"
                              spanMessage={t("general.optional")}
                              id="occupation_description"
                              name="occupation_description"
                              placeholder={t(
                                "user.views.occupation_description"
                              )}
                              value={values.occupation_description}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("user.views.business")}
                              span={t("general.optional")}
                              id="company"
                              name="company"
                              type="text"
                              placeholder={t("user.views.business")}
                              value={values.company}
                              onChange={handleChange}
                              error={!!errors.company}
                              textError={errors.company}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithCornerHint
                              label={t("user.views.birthday")}
                              span={t("general.mandatory")}
                              id="birthday"
                              name="birthday"
                              type="date"
                              placeholder={t("user.views.birthday")}
                              value={values?.birthday || ""}
                              onChange={handleChange}
                              error={errors?.birthday && touched?.birthday}
                              textError={errors?.birthday}
                            />
                          </div>

                          <div className="col-span-full">
                            <div className="relative flex items-start">
                              <div className="flex h-6 items-center">
                                <input
                                  id="is_chat_responsible"
                                  checked={
                                    values.is_chat_responsible as boolean
                                  }
                                  onChange={handleChange}
                                  name="is_chat_responsible"
                                  type="checkbox"
                                  aria-describedby=""
                                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                              </div>
                              <div className="ml-3 text-sm leading-6">
                                <label
                                  htmlFor="is_chat_responsible"
                                  className="font-normal text-gray-900"
                                >
                                  {t("user.views.chat")}
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="col-span-full">
                            <Select
                              name="sex"
                              span={t("general.mandatory")}
                              selected={optionsSex.find(
                                (sex) => sex.id == values.sex
                              )}
                              setSelected={(value) =>
                                setFieldValue("sex", value.id)
                              }
                              options={optionsSex}
                              tittle={t("user.views.sex")}
                              error={errors?.sex && touched?.sex}
                              textError={
                                Boolean(errors?.sex) && Boolean(touched?.sex)
                                  ? errors?.sex
                                  : ""
                              }
                            />
                          </div>
                          <div className="col-span-full">
                            <Select
                              name="objective"
                              span={t("general.optional")}
                              selected={optionsObjetives.find(
                                (objetive) => objetive.id == values.objective
                              )}
                              setSelected={(value) =>
                                setFieldValue("objective", value.id)
                              }
                              options={optionsObjetives}
                              tittle={t("user.views.objective")}
                            />
                          </div>
                          <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4">
                            <div className="relative flex items-start">
                              <div className="flex h-6 items-center">
                                <input
                                  checked={values.accept_clauses}
                                  disabled={true}
                                  id="accept_clauses"
                                  name="accept_clauses"
                                  type="checkbox"
                                  aria-describedby=""
                                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-600 disabled:cursor-not-allowed"
                                />
                              </div>
                              <div className="ml-3 text-sm leading-6">
                                <label
                                  htmlFor="accept_clauses"
                                  className="font-normal text-gray-900"
                                >
                                  {t("user.views.accept_clauses_norms")}
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4">
                            <div className="relative flex items-start">
                              <div className="flex h-6 items-center">
                                <input
                                  checked={values.sufficiently_informed}
                                  disabled={true}
                                  id="sufficiently_informed"
                                  name="sufficiently_informed"
                                  type="checkbox"
                                  aria-describedby=""
                                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-600 disabled:cursor-not-allowed"
                                />
                              </div>
                              <div className="ml-3 text-sm leading-6">
                                <label
                                  htmlFor="sufficiently_informed"
                                  className="font-normal text-gray-900"
                                >
                                  {t("user.views.informed_participation")}
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4">
                            <div className="relative flex items-start">
                              <div className="flex h-6 items-center">
                                <input
                                  checked={values.receive_communications}
                                  onChange={handleChange}
                                  id="receive_communications"
                                  name="receive_communications"
                                  type="checkbox"
                                  aria-describedby=""
                                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-600"
                                />
                              </div>
                              <div className="ml-3 text-sm leading-6">
                                <label
                                  htmlFor="receive_communications"
                                  className="font-normal text-gray-900"
                                >
                                  {t("user.views.receive_communications")}
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4">
                            <div className="relative flex items-start">
                              <div className="flex h-6 items-center">
                                <input
                                  checked={values.image_rights}
                                  onChange={handleChange}
                                  id="image_rights"
                                  name="image_rights"
                                  type="checkbox"
                                  aria-describedby=""
                                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-600"
                                />
                              </div>
                              <div className="ml-3 text-sm leading-6">
                                <label
                                  htmlFor="image_rights"
                                  className="font-normal text-gray-900"
                                >
                                  {t("user.views.image_rights")}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogCreate>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
};
